import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { track } from '@amplitude/analytics-browser'

import { collectionTranslateAction } from 'src/reducers/course-collection/action'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import { userLimitsUpdateCredits } from 'src/reducers/user-limits/action'
import { selectCurrentCredits } from 'src/reducers/user-limits/selector'
import { collectionTranslationCostEstimateService } from 'src/api/course-collection'
import { languages } from 'src/model/languages'

import styles from './styles'

const STEPS = ['Configure Translation', 'Translate']

const CollectionTranslateModal = ({
  classes,
  collectionId,
  collectionName,
  refresh,
}) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const currentCredits = useSelector(selectCurrentCredits)

  const [activeStep, setActiveStep] = useState(0)
  const [selectedLanguage, setSelectedLanguage] = useState('es')
  const [estimate, setEstimate] = useState(null)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [estimateError, setEstimateError] = useState(null)

  // Fetch the credit-cost estimate the first time the user lands on step 2.
  // Cache it so going Back/Next doesn't refetch. We intentionally exclude
  // `estimate` and `estimateLoading` from deps — including them would make this
  // effect re-run on the very state updates it triggers, and the cleanup would
  // cancel the in-flight request before its .then() can store the result.
  useEffect(() => {
    if (activeStep !== 1 || !collectionId) return

    let cancelled = false
    setEstimateLoading(true)
    setEstimateError(null)

    collectionTranslationCostEstimateService(collectionId)
      .then(response => {
        if (cancelled) return
        const data = response?.data ?? response
        setEstimate({
          pageCount: data?.pageCount ?? 0,
          creditsPerPage: data?.creditsPerPage ?? 5,
          totalCredits: data?.totalCredits ?? 0,
        })
      })
      .catch(() => {
        if (cancelled) return
        setEstimateError('Could not estimate translation cost.')
      })
      .finally(() => {
        if (!cancelled) setEstimateLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, collectionId])

  const handleNext = () => setActiveStep(prev => prev + 1)
  const handleBack = () => setActiveStep(prev => prev - 1)
  const handleClose = () => dispatch(messageBoxHideAction())

  const hasEnoughCredits =
    !estimate || estimate.totalCredits === 0 || currentCredits >= estimate.totalCredits

  const handleTranslate = () => {
    if (!collectionId || !selectedLanguage) return

    const targetLanguageName =
      languages.find(l => l.countryCode === selectedLanguage)?.name ||
      selectedLanguage

    if (!hasEnoughCredits) {
      track('collection_translation_insufficient_credits', {
        collection_id: collectionId,
        target_language: selectedLanguage,
        page_count: estimate?.pageCount,
        required_credits: estimate?.totalCredits,
        current_credits: currentCredits,
      })
      return
    }

    track('collection_translation_started', {
      collection_id: collectionId,
      target_language: selectedLanguage,
      target_language_name: targetLanguageName,
      page_count: estimate?.pageCount,
      total_credits: estimate?.totalCredits,
    })

    enqueueSnackbar(`Translating to ${targetLanguageName}…`, { variant: 'info' })

    dispatch(
      collectionTranslateAction(collectionId, selectedLanguage, () => {
        if (typeof refresh === 'function') refresh()
      }),
    )

    // Optimistically decrement the header credit badge — backend has just charged
    // the same amount via HandleUserLimitAsync.
    if (estimate?.totalCredits > 0) {
      dispatch(userLimitsUpdateCredits(estimate.totalCredits))
    }

    handleClose()
  }

  const renderCostNotice = () => {
    if (estimateLoading) {
      return (
        <Box display="flex" alignItems="center" gap="8px" justifyContent="center" mt={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            Calculating cost…
          </Typography>
        </Box>
      )
    }

    if (estimateError) {
      return (
        <Box className={classes.noticeError} mt={2}>
          <Typography variant="body2">{estimateError}</Typography>
        </Box>
      )
    }

    if (!estimate) return null

    if (estimate.pageCount === 0) {
      return (
        <Box className={classes.noticeInfo} mt={2}>
          <Typography variant="body2">
            This learning path has no pages to translate. No credits will be charged.
          </Typography>
        </Box>
      )
    }

    return (
      <Box
        className={hasEnoughCredits ? classes.noticeInfo : classes.noticeError}
        mt={2}
      >
        <Typography variant="body2">
          Your collection contains{' '}
          <b style={{ color: 'rgb(1, 95, 251)' }}>{estimate.pageCount} pages</b>.
          Translating it will cost{' '}
          <b style={{ color: 'rgb(1, 95, 251)' }}>
            {estimate.totalCredits} credits
          </b>{' '}
          ({estimate.creditsPerPage} credits per page). Do you want to continue?
        </Typography>
        {!hasEnoughCredits && (
          <Typography
            variant="body2"
            style={{ marginTop: 8, fontWeight: 600, color: '#B91C1C' }}
          >
            Insufficient credits. You have {currentCredits} credits.
          </Typography>
        )}
      </Box>
    )
  }

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box display="flex" flexDirection="column" gap="16px">
            <Typography variant="h6">Configure Translation Settings</Typography>
            <Typography variant="body2" color="textSecondary">
              Translating <b>{collectionName || 'collection'}</b>. Set the target
              language for the translation.
            </Typography>

            <FormControl variant="outlined" fullWidth style={{ marginTop: '8px' }}>
              <InputLabel>Target Language</InputLabel>
              <Select
                value={selectedLanguage}
                onChange={e => setSelectedLanguage(e.target.value)}
                label="Target Language"
              >
                {languages.map(lang => (
                  <MenuItem key={lang.countryCode} value={lang.countryCode}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={3} display="flex" justifyContent="space-between">
              <Button onClick={handleClose} className={classes.secondaryButton}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                className={classes.primaryButton}
              >
                Next Step
              </Button>
            </Box>
          </Box>
        )
      case 1: {
        const startDisabled =
          estimateLoading || !!estimateError || !hasEnoughCredits
        return (
          <Box
            display="flex"
            flexDirection="column"
            gap="16px"
            alignItems="center"
            textAlign="center"
            py={2}
          >
            <Typography variant="h5" style={{ fontWeight: 700 }}>
              Ready to Translate
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ maxWidth: 440 }}
            >
              You are about to translate{' '}
              <b>{collectionName || 'this learning path'}</b> to{' '}
              <b>
                {languages.find(l => l.countryCode === selectedLanguage)?.name ||
                  selectedLanguage}
              </b>
              . The translation will run in the background and the new
              learning path will appear in your list.
            </Typography>

            {renderCostNotice()}

            <Box mt={3} display="flex" gap="12px" justifyContent="center">
              <Button onClick={handleBack} className={classes.secondaryButton}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTranslate}
                disabled={startDisabled}
                className={classes.primaryButton}
                style={{ padding: '0 32px' }}
              >
                Start Translation
              </Button>
            </Box>
          </Box>
        )
      }
      default:
        return null
    }
  }

  return (
    <div className={classes.root}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        className={classes.stepper}
      >
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div className={classes.stepBody}>{renderStep()}</div>
    </div>
  )
}

export default withStyles(styles)(CollectionTranslateModal)
